import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { motion, AnimatePresence } from 'framer-motion';
import { tokenomicsConfig } from '@/data/tokenomicsData';
import { 
  Droplets, Users, Megaphone, Landmark, FlaskConical, 
  Vote, TrendingUp, Users2, Lock, Unlock, ChevronRight 
} from 'lucide-react';

// Icon mapping
const iconMap = {
  Droplets, Users, Megaphone, Landmark, FlaskConical, 
  Vote, TrendingUp, Users2
};

export const AllocationPieChart = ({ onAllocationSelect, selectedAllocation }) => {
  const svgRef = useRef(null);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 500, height: 500 });
  
  // Responsive dimensions
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current?.parentElement) {
        const parent = svgRef.current.parentElement;
        const size = Math.min(parent.clientWidth, 500);
        setDimensions({ width: size, height: size });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // D3 Chart rendering
  useEffect(() => {
    if (!svgRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const { width, height } = dimensions;
    const radius = Math.min(width, height) / 2 - 40;
    const innerRadius = radius * 0.55;
    
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);
    
    // Pie generator
    const pie = d3.pie()
      .value(d => d.percentage)
      .sort(null)
      .padAngle(0.02);
    
    // Arc generators
    const arc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(radius)
      .cornerRadius(4);
    
    const arcHover = d3.arc()
      .innerRadius(innerRadius - 5)
      .outerRadius(radius + 10)
      .cornerRadius(6);
    
    // Create pie data
    const pieData = pie(tokenomicsConfig.allocations);
    
    // Add glow filter
    const defs = svg.append('defs');
    const filter = defs.append('filter')
      .attr('id', 'glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '4')
      .attr('result', 'coloredBlur');
    
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    
    // Draw segments
    const segments = g.selectAll('.segment')
      .data(pieData)
      .enter()
      .append('g')
      .attr('class', 'segment');
    
    segments.append('path')
      .attr('d', arc)
      .attr('fill', d => d.data.color)
      .attr('stroke', 'hsl(215, 28%, 7%)')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .style('filter', d => 
        (hoveredSegment === d.data.id || selectedAllocation?.id === d.data.id) 
          ? 'url(#glow)' 
          : 'none'
      )
      .style('opacity', d => 
        selectedAllocation && selectedAllocation.id !== d.data.id ? 0.5 : 1
      )
      .on('mouseenter', function(event, d) {
        setHoveredSegment(d.data.id);
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arcHover)
          .style('filter', 'url(#glow)');
      })
      .on('mouseleave', function(event, d) {
        setHoveredSegment(null);
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arc)
          .style('filter', selectedAllocation?.id === d.data.id ? 'url(#glow)' : 'none');
      })
      .on('click', function(event, d) {
        onAllocationSelect?.(d.data);
      });
    
    // Add percentage labels
    segments.append('text')
      .attr('transform', d => `translate(${arc.centroid(d)})`)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('fill', 'hsl(215, 28%, 7%)')
      .style('pointer-events', 'none')
      .text(d => `${d.data.percentage}%`);
    
    // Center text
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '-0.5em')
      .style('font-size', '14px')
      .style('fill', 'hsl(210, 15%, 60%)')
      .text('Total Supply');
    
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '1em')
      .style('font-size', '24px')
      .style('font-weight', '700')
      .style('fill', 'hsl(192, 75%, 50%)')
      .text('100%');
    
  }, [dimensions, hoveredSegment, selectedAllocation, onAllocationSelect]);
  
  const activeData = hoveredSegment 
    ? tokenomicsConfig.allocations.find(a => a.id === hoveredSegment)
    : selectedAllocation;
  
  return (
    <div className="flex flex-col lg:flex-row items-center gap-8">
      {/* Chart */}
      <div className="relative flex-shrink-0">
        <svg ref={svgRef} className="overflow-visible" />
      </div>
      
      {/* Legend and Info Panel */}
      <div className="flex-1 space-y-4 w-full max-w-md">
        {/* Legend */}
        <div className="grid grid-cols-2 gap-2">
          {tokenomicsConfig.allocations.map((allocation) => {
            const Icon = iconMap[allocation.icon];
            const isActive = activeData?.id === allocation.id;
            
            return (
              <button
                key={allocation.id}
                onClick={() => onAllocationSelect?.(allocation)}
                className={`
                  flex items-center gap-2 p-2 rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-primary/10 border border-primary/30' 
                    : 'bg-card/50 border border-transparent hover:bg-card hover:border-border'
                  }
                `}
              >
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: allocation.color }}
                />
                <span className="text-xs text-muted-foreground truncate">
                  {allocation.name}
                </span>
                <span className="text-xs font-semibold text-foreground ml-auto">
                  {allocation.percentage}%
                </span>
              </button>
            );
          })}
        </div>
        
        {/* Info Panel */}
        <AnimatePresence mode="wait">
          {activeData && (
            <motion.div
              key={activeData.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="glass rounded-xl p-4 border-glow"
            >
              <div className="flex items-start gap-3">
                {(() => {
                  const Icon = iconMap[activeData.icon];
                  return Icon ? (
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${activeData.color}20` }}
                    >
                      <Icon 
                        className="w-5 h-5" 
                        style={{ color: activeData.color }}
                      />
                    </div>
                  ) : null;
                })()}
                <div className="flex-1">
                  <h4 className="font-heading font-semibold text-foreground">
                    {activeData.name}
                  </h4>
                  <p className="text-2xl font-bold mt-1" style={{ color: activeData.color }}>
                    {activeData.percentage}%
                  </p>
                </div>
                <div className={`
                  flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                  ${activeData.unlockedAtTGE 
                    ? 'bg-success/20 text-success' 
                    : 'bg-warning/20 text-warning'
                  }
                `}>
                  {activeData.unlockedAtTGE ? (
                    <>
                      <Unlock className="w-3 h-3" />
                      Unlocked
                    </>
                  ) : (
                    <>
                      <Lock className="w-3 h-3" />
                      Locked
                    </>
                  )}
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mt-3">
                {activeData.description}
              </p>
              
              {activeData.vesting && (
                <div className="mt-3 p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Vesting: </span>
                    {activeData.vesting.cliffMonths} month cliff, 
                    {activeData.vesting.vestingMonths} month linear vesting
                  </p>
                </div>
              )}
              
              {activeData.breakdown && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Breakdown:
                  </p>
                  <div className="space-y-1">
                    {activeData.breakdown.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-xs text-muted-foreground flex-1">
                          {item.name}
                        </span>
                        <span className="text-xs font-medium text-foreground">
                          {item.percentage}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {activeData.breakdown && (
                <button className="flex items-center gap-1 text-xs text-primary mt-3 hover:underline">
                  View detailed breakdown
                  <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AllocationPieChart;
